import { Button, Chip, Input, Label, TextField } from "@heroui/react";
import { Segment } from "@heroui-pro/react";
import { Eye, Ban, RotateCcw, CheckCircle2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { EventsModal } from "../modal/events";

import type { DataGridColumn, SortDescriptor } from "@/components/kit";
import type {
  ZeroResultQuery,
  ZeroResultQueryStatus,
  ZeroResultSearchFilters,
} from "@/types";

import {
  AppliedFilters,
  DataTable,
  FiltersPopover,
  PlainDateField,
  RowMenu,
  SearchBox,
} from "@/components/kit";
import { Toolbar, formatDayTime } from "@/components/sales/shared";
import { useDebounce } from "@/hooks/useDebounce";

interface ZeroResultSearchTableProps {
  items: ZeroResultQuery[];
  page: number;
  totalItems: number;
  changePage: (page: number) => void;
  isLoading: boolean;
  limit: number;
  onLimitChange: (limit: number) => void;
  filters: ZeroResultSearchFilters;
  setFilter: <K extends keyof ZeroResultSearchFilters>(
    key: K,
    value: ZeroResultSearchFilters[K],
  ) => void;
  setFilters: (filters: Partial<ZeroResultSearchFilters>) => void;
  clearFilters: () => void;
  onStatusChange: (id: string, status: ZeroResultQueryStatus) => void;
  isStatusUpdating?: boolean;
}

const STATUS_TABS: Array<{ id: string; label: string }> = [
  { id: "all", label: "All" },
  { id: "active", label: "Active" },
  { id: "resolved", label: "Resolved" },
  { id: "ignored", label: "Ignored" },
];

const STATUS_COLOR = {
  active: "warning",
  resolved: "success",
  ignored: "default",
} as const;

const STATUS_LABEL: Record<ZeroResultQueryStatus, string> = {
  active: "Active",
  resolved: "Resolved",
  ignored: "Ignored",
};

const asNumber = (text: string) => (text ? parseInt(text, 10) : undefined);

export function ZeroResultSearchTable({
  items,
  page,
  totalItems,
  changePage,
  isLoading,
  limit,
  onLimitChange,
  filters,
  setFilter,
  setFilters,
  clearFilters,
  onStatusChange,
  isStatusUpdating,
}: ZeroResultSearchTableProps) {
  const [localSearchTerm, setLocalSearchTerm] = useState(
    filters.searchTerm || "",
  );
  const debouncedSearchTerm = useDebounce(localSearchTerm, 500);

  useEffect(() => {
    if (debouncedSearchTerm !== filters.searchTerm) {
      setFilter("searchTerm", debouncedSearchTerm || undefined);
    }
  }, [debouncedSearchTerm]);

  const [selectedQuery, setSelectedQuery] = useState<ZeroResultQuery | null>(
    null,
  );
  const [draft, setDraft] = useState({
    minSearchCount: filters.minSearchCount?.toString() ?? "",
    minUniqueUsers: filters.minUniqueUsers?.toString() ?? "",
    dateFrom: filters.dateFrom ?? "",
    dateTo: filters.dateTo ?? "",
  });

  const sort: SortDescriptor = {
    column: filters.sort ?? "totalSearchCount",
    direction: filters.sortOrder === "ASC" ? "ascending" : "descending",
  };

  const applied = [
    filters.minSearchCount !== undefined
      ? {
          id: "minSearchCount",
          label: "Min searches",
          value: String(filters.minSearchCount),
        }
      : null,
    filters.minUniqueUsers !== undefined
      ? {
          id: "minUniqueUsers",
          label: "Min users",
          value: String(filters.minUniqueUsers),
        }
      : null,
    filters.dateFrom
      ? { id: "dateFrom", label: "From", value: filters.dateFrom }
      : null,
    filters.dateTo
      ? { id: "dateTo", label: "To", value: filters.dateTo }
      : null,
  ].filter((entry): entry is NonNullable<typeof entry> => entry !== null);

  const handleViewEvents = useCallback(
    (query: ZeroResultQuery) => setSelectedQuery(query),
    [],
  );

  const columns = useMemo<DataGridColumn<ZeroResultQuery>[]>(
    () => [
      {
        id: "searchTerm",
        header: "Search term",
        isRowHeader: true,
        allowsSorting: true,
        minWidth: 220,
        cell: (query) => (
          <span className="font-medium">{query.searchTerm}</span>
        ),
      },
      {
        id: "status",
        header: "Status",
        width: 130,
        cell: (query) => (
          <Chip color={STATUS_COLOR[query.status]} variant="soft">
            {STATUS_LABEL[query.status]}
          </Chip>
        ),
      },
      {
        id: "totalSearchCount",
        header: "Total searches",
        allowsSorting: true,
        align: "end",
        cell: (query) => (
          <span className="font-medium tabular-nums">
            {query.totalSearchCount}
          </span>
        ),
      },
      {
        id: "uniqueUserCount",
        header: "Unique users",
        allowsSorting: true,
        align: "end",
        cell: (query) => (
          <span className="tabular-nums">{query.uniqueUserCount}</span>
        ),
      },
      {
        id: "lastSearchedAt",
        header: "Last searched",
        allowsSorting: true,
        minWidth: 180,
        cell: (query) =>
          query.lastSearchedAt ? (
            <span className="text-muted tabular-nums">
              {formatDayTime(query.lastSearchedAt)}
            </span>
          ) : (
            <span className="text-muted">{"—"}</span>
          ),
      },
      {
        id: "actions",
        header: <span className="sr-only">Actions</span>,
        align: "end",
        width: 200,
        cell: (query) => {
          const primary: ZeroResultQueryStatus =
            query.status === "active" ? "resolved" : "active";
          const others = (["resolved", "ignored", "active"] as const).filter(
            (status) => status !== query.status && status !== primary,
          );
          const verb: Record<ZeroResultQueryStatus, string> = {
            active: "Reactivate",
            resolved: "Resolve",
            ignored: "Ignore",
          };
          const icon: Record<ZeroResultQueryStatus, React.ReactNode> = {
            active: <RotateCcw className="size-4" />,
            resolved: <CheckCircle2 className="size-4" />,
            ignored: <Ban className="size-4" />,
          };

          return (
            <div className="flex items-center justify-end gap-1">
              <Button
                isDisabled={isStatusUpdating}
                size="sm"
                variant="outline"
                onPress={() => onStatusChange(query.id, primary)}
              >
                {verb[primary]}
              </Button>
              <RowMenu
                items={[
                  {
                    id: "events",
                    label: "View Events",
                    icon: <Eye className="size-4" />,
                    onAction: () => handleViewEvents(query),
                  },
                  ...others.map((status) => ({
                    id: status,
                    label: verb[status],
                    icon: icon[status],
                    onAction: () => onStatusChange(query.id, status),
                  })),
                ]}
              />
            </div>
          );
        },
      },
    ],
    [handleViewEvents, isStatusUpdating, onStatusChange],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <Toolbar
        end={
          <>
            <SearchBox
              className="w-80"
              placeholder="Search queries"
              value={localSearchTerm}
              onChange={setLocalSearchTerm}
            />
            <FiltersPopover
              activeCount={applied.length}
              onApply={() =>
                setFilters({
                  minSearchCount: asNumber(draft.minSearchCount),
                  minUniqueUsers: asNumber(draft.minUniqueUsers),
                  dateFrom: draft.dateFrom || undefined,
                  dateTo: draft.dateTo || undefined,
                })
              }
              onClearAll={() => {
                setDraft({
                  minSearchCount: "",
                  minUniqueUsers: "",
                  dateFrom: "",
                  dateTo: "",
                });
                setFilters({
                  minSearchCount: undefined,
                  minUniqueUsers: undefined,
                  dateFrom: undefined,
                  dateTo: undefined,
                });
              }}
            >
              <div className="grid grid-cols-2 gap-3 px-3">
                <TextField
                  className="flex flex-col gap-1.5"
                  type="number"
                  value={draft.minSearchCount}
                  onChange={(value) =>
                    setDraft({ ...draft, minSearchCount: value })
                  }
                >
                  <Label>Min searches</Label>
                  <Input min={0} placeholder="Any" variant="secondary" />
                </TextField>
                <TextField
                  className="flex flex-col gap-1.5"
                  type="number"
                  value={draft.minUniqueUsers}
                  onChange={(value) =>
                    setDraft({ ...draft, minUniqueUsers: value })
                  }
                >
                  <Label>Min unique users</Label>
                  <Input min={0} placeholder="Any" variant="secondary" />
                </TextField>
              </div>
              <div className="grid grid-cols-2 gap-3 px-3">
                <PlainDateField
                  label="From"
                  name="dateFrom"
                  value={draft.dateFrom}
                  onChange={(dateFrom) => setDraft({ ...draft, dateFrom })}
                />
                <PlainDateField
                  label="To"
                  name="dateTo"
                  value={draft.dateTo}
                  onChange={(dateTo) => setDraft({ ...draft, dateTo })}
                />
              </div>
            </FiltersPopover>
          </>
        }
      >
        <div className="isolate">
          <Segment
            aria-label="Status"
            selectedKey={filters.status ?? "all"}
            onSelectionChange={(key) =>
              setFilter(
                "status",
                key === "all"
                  ? undefined
                  : (String(key) as ZeroResultQueryStatus),
              )
            }
          >
            {STATUS_TABS.map((tab, index) => (
              <Segment.Item key={tab.id} id={tab.id}>
                {index > 0 ? <Segment.Separator /> : null}
                {tab.label}
              </Segment.Item>
            ))}
          </Segment>
        </div>
        <AppliedFilters
          filters={applied}
          onClearAll={clearFilters}
          onRemove={(id) => {
            setDraft({ ...draft, [id]: "" });
            setFilters({ [id]: undefined });
          }}
        />
      </Toolbar>

      <DataTable
        columns={columns}
        compact
        empty={{
          title: "No zero-result searches found",
          description: "Try a different search or clear the filters.",
        }}
        getRowId={(query) => query.id}
        isLoading={isLoading}
        label="Zero result searches"
        pagination={{
          page,
          pageSize: limit,
          total: totalItems,
          onPageChange: changePage,
          onPageSizeChange: onLimitChange,
          pageSizes: [10, 25, 50],
        }}
        rows={items}
        sort={sort}
        onSortChange={(next) =>
          setFilters({
            sort: String(next.column),
            sortOrder: next.direction === "ascending" ? "ASC" : "DESC",
          })
        }
      />

      <EventsModal
        isOpen={selectedQuery !== null}
        query={selectedQuery}
        onClose={() => setSelectedQuery(null)}
      />
    </div>
  );
}
